import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import {
  recordShowOriginalDocIdsAtom,
  recordTranslatingDocIdsAtom,
  recordTranslationEntriesAtom,
} from '@/components/Record/TalkFLow/translationAtoms';
import type { TranslatableField } from '@/components/Record/TalkFLow/hooks/useRecordTranslationSource';

/**
 * 번역 결과 읽기 전용 접근자.
 *
 * 제목(Header) / AI 요약(AiSummary) / 본문·메모(Contents) 세 곳이 같은 판정을 반복하므로
 * 문서별 조회와 필드 fallback 을 여기 한 곳에 모았다.
 *
 * 상태가 docId 로 나뉘어 있어(translationAtoms 주석 참고) 동시에 열린 다른 문서의
 * 번역이 이 화면에 섞이지 않는다.
 *
 * `pick` 은 번역이 꺼져 있거나 원문 보기 상태이거나 해당 필드 번역이 비어 있으면
 * 원문을 그대로 돌려준다.
 */
const useRecordTranslationView = (docId: string) => {
  const entries = useAtomValue(recordTranslationEntriesAtom);
  const translatingDocIds = useAtomValue(recordTranslatingDocIdsAtom);
  const showOriginalDocIds = useAtomValue(recordShowOriginalDocIdsAtom);

  const entry = docId ? entries[docId] : undefined;
  const isTranslating = Boolean(docId && translatingDocIds[docId]);
  const showOriginal = Boolean(docId && showOriginalDocIds[docId]);

  const hasCurrentResult = Boolean(entry?.isApplied);
  const isActive = hasCurrentResult && !showOriginal;

  const pick = useCallback(
    (field: TranslatableField, original: string) => {
      if (!isActive) return original;
      return entry?.[field] || original;
    },
    [isActive, entry],
  );

  return {
    /** 이 문서의 번역 결과 (없으면 undefined) */
    entry,
    /** 번역본이 실제로 화면에 적용되는 상태 (원문 보기 중이면 false) */
    isActive,
    /** 번역이 켜져 있는 상태 — 원문 보기 중에도 true (편집 잠금 판정용) */
    hasCurrentResult,
    /** 이 문서의 번역 요청이 진행 중 */
    isTranslating,
    /** 이 문서를 원문 보기로 전환한 상태 */
    showOriginal,
    /** 상태 띠가 화면에 뜨는 상태 — 헤더 높이가 늘어나므로 본문 여백 계산에 쓴다 */
    isBannerVisible: isTranslating || hasCurrentResult,
    /** 현재 적용된 도착 언어 (번역이 꺼져 있으면 undefined) */
    targetLanguage: hasCurrentResult ? entry?.targetLanguage : undefined,
    pick,
  };
};

export default useRecordTranslationView;
